
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronsUpDown, PlusCircle, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState, useRef, useEffect } from 'react';
import CreateOrganizationForm from './CreateOrganizationForm';
import { useActivityLogger } from '@/hooks/use-activity-logger';
import { toast } from 'sonner';

const OrganizationSelector = () => {
  const { organizations, currentOrganization, setCurrentOrganization, refreshOrganizations } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const { logActivity } = useActivityLogger();
  const hasInitialized = useRef(false);
  
  // Add effect to refresh organizations when component mounts if no orgs are loaded
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        
        if (organizations.length === 0) {
          setIsLoadingOrgs(true);
          try {
            await refreshOrganizations();
          } catch (error) {
            console.error("Error loading organizations:", error);
            toast.error("Failed to load organizations. Please try again.");
          } finally {
            setIsLoadingOrgs(false);
          }
        }
      }
    };
    
    loadOrganizations();
  }, [organizations.length, refreshOrganizations]);

  const handleOrganizationSelect = async (org: any) => {
    try {
      setCurrentOrganization(org);
      
      // Only log activity if the organization exists and user is authenticated
      if (org && org.organization && org.organization.id) {
        await logActivity({
          action: 'access',
          resourceType: 'organization',
          resourceId: org.organization.id,
          details: { name: org.organization.name }
        });
        toast.success(`Switched to ${org.organization.name}`);
      }
    } catch (error) {
      console.error("Error selecting organization:", error);
    }
  };

  const handleRefresh = async () => {
    setIsLoadingOrgs(true);
    try {
      await refreshOrganizations();
      toast.success("Organizations refreshed");
    } catch (error) {
      console.error("Error refreshing organizations:", error);
      toast.error("Failed to refresh organizations");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            {currentOrganization ? currentOrganization.organization.name : 'Select Organization'}
            <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <div className="flex items-center justify-between px-2">
            <DropdownMenuLabel>Organizations</DropdownMenuLabel>
            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={isLoadingOrgs}>
              <RefreshCw className={`h-4 w-4 ${isLoadingOrgs ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <DropdownMenuSeparator />
          {isLoadingOrgs ? (
            <DropdownMenuItem disabled>Loading organizations...</DropdownMenuItem>
          ) : organizations.length > 0 ? (
            organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                className="cursor-pointer"
                onClick={() => handleOrganizationSelect(org)}
              >
                {org.organization.name}
                <span className="ml-2 text-xs text-muted-foreground capitalize">
                  ({org.role.replace('_', ' ')})
                </span>
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem disabled>No organizations found</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="cursor-pointer" 
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Create New Organization
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Create a new organization as the Primary Founder.
            </DialogDescription>
          </DialogHeader>
          <CreateOrganizationForm onSuccess={() => {
            setIsCreateDialogOpen(false);
            refreshOrganizations();
          }} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrganizationSelector;
